import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Sun, Moon, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../../js/external-js/config/main_config';
import '@/styles/login.css';

// Theme types
type LoginTheme = 'light' | 'dark' | 'system';

// Get initial theme from localStorage or default to system
const getInitialTheme = (): LoginTheme => {
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem('login-theme');
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
  }
  return 'system';
};

// Apply theme to document
const applyTheme = (theme: LoginTheme) => {
  if (typeof document === 'undefined') return;
  
  if (theme === 'system') {
    document.documentElement.removeAttribute('data-login-theme');
  } else {
    document.documentElement.setAttribute('data-login-theme', theme);
  }
};

const PendingApproval = () => {
  const navigate = useNavigate();
  const [theme, setTheme] = useState<LoginTheme>(getInitialTheme);
  const [tenancyName, setTenancyName] = useState<string>('');
  const [userEmail, setUserEmail] = useState<string>('');

  // Get pending approval info from sessionStorage
  useEffect(() => {
    const pendingInfo = sessionStorage.getItem('pendingApprovalInfo');
    if (pendingInfo) {
      try {
        const info = JSON.parse(pendingInfo);
        setTenancyName(info.tenancyName || '');
      } catch (e) {
        console.error('Failed to parse pending approval info:', e);
      }
    }
    
    // Get current user email
    const user = auth.currentUser;
    if (user?.email) {
      setUserEmail(user.email);
    }
  }, []);

  // Apply theme on mount and when theme changes
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => {
      const next = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem('login-theme', next);
      return next;
    });
  };

  const isDark = theme === 'dark' || (theme === 'system' && typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  const handleSignOut = async () => {
    try {
      // Clear pending approval info
      sessionStorage.removeItem('pendingApprovalInfo');
      await auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const handleRefresh = () => {
    // Clear cached pending info so Layout will do a fresh check
    sessionStorage.removeItem('pendingApprovalInfo');
    // Navigate to dashboard - Layout will check actual status
    window.location.href = '/dashboard';
  };

  return (
    <div className="auth-container pending-approval-container">
      {/* Single centered view */}
      <div className="pending-approval-view">
        {/* Animated gradient background */}
        <div className="gradient-overlay"></div>
        <div className="glow-1"></div>
        <div className="glow-2"></div>
        
        {/* Theme toggle */}
        <button className="theme-toggle" onClick={toggleTheme} aria-label="Toggle theme">
          {isDark ? <Sun /> : <Moon />}
        </button>
        
        <div className="pending-approval-content">
          {/* Branding */}
          <div className="pending-branding">
            <img 
              src="/assets/ducks/compliant.svg" 
              alt="de.iterate Duck" 
              className="pending-duck" 
            />
            <h1 className="pending-brand-title">de.iterate</h1>
          </div>
          
          {/* Clock icon */}
          <div className="pending-icon">
            <Clock size={48} strokeWidth={1.5} />
          </div>
          
          <h2 className="pending-title">Approval Pending</h2>
          <p className="pending-subtitle">Your request to join {tenancyName || 'the organisation'} is awaiting approval</p>
          
          <div className="pending-info">
            <div className="pending-card">
              <p className="pending-email">
                <strong>Account:</strong> {userEmail || 'Your account'}
              </p>
              <p className="pending-status">
                <strong>Status:</strong> <span className="status-badge">Pending Review</span>
              </p>
              {tenancyName && (
                <p className="pending-org">
                  <strong>Organisation:</strong> {tenancyName}
                </p>
              )}
            </div>
            
            <p className="pending-message">
              An administrator from {tenancyName || 'the organisation'} will review your request. 
              You'll receive an email notification once your access has been approved.
            </p>
          </div>

          <div className="pending-actions">
            <Button 
              onClick={handleRefresh}
              className="sign-in-button"
            >
              Check Status
            </Button>
            <Button 
              variant="outline" 
              onClick={handleSignOut}
              className="w-full mt-3"
            >
              Sign Out
            </Button>
          </div>

          <p className="terms-text">
              Need help? Contact your organisation administrator or{' '}
              <a href="mailto:support@deiterate.com">support@deiterate.com</a>
            </p>
        </div>
      </div>
    </div>
  );
};

export default PendingApproval;
